import { inject } from '@angular/core';
import { catchError, concatMap, exhaustMap, map, of, switchMap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { addOverpack, addOverpackSuccess, assignOverpack, assignOverpackFailure, assignOverpackSuccess, duplicateCompositePackage, duplicateCompositePackageFailure, duplicateCompositePackageSuccess, duplicateSinglePackage, duplicateSinglePackageFailure, duplicateSinglePackageSuccess, initializeInnerPackage, initializeInnerPackageFailure, initializeInnerPackageSuccess, initializeSinglePackage, initializeSinglePackageFailure, initializeSinglePackageSuccess, loadPackages, loadPackagesFailure, loadPackagesSuccess, loadPackagingTypes, loadPackagingTypesBulkSuccess, loadPackagingTypesFailure, loadPackagingTypesSuccess, loadPackagingTypesTankSuccess, loadUnPackagingTypes, loadUnPackagingTypesFailure, loadUnPackagingTypesSuccess, removeCompositePackage, removeCompositePackageFailure, removeCompositePackageSuccess, removeOverpack, removeOverpackSuccess, removeSinglePackage, removeSinglePackageFailure, removeSinglePackageSuccess, saveCompositePackage, saveCompositePackageFailure, saveCompositePackageSuccess, saveCompositePackageUpdateSuccess, saveSinglePackage, saveSinglePackageFailure, saveSinglePackageSuccess, saveSinglePackageUpdateSuccess, updateOverpack, updateOverpackSuccess } from './package-list.actions';
import { DangerousGoodItem, TransportMode } from '@hazmatsuite-next/ui';
import { EasyDgdService, EnumPackageType, PackagingType, PackagingModel, SinglePackagingModel, InnerPackagingModel, CompositePackagingModel, OverPackagingModel } from '@hazmatsuite-next/easydgd-lib';

export const loadPackagesEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(loadPackages),
      exhaustMap(() =>
        easyDgdService.loadPackages().pipe(
          map((packages) => {
            return loadPackagesSuccess({ packages })
          }),
          catchError((error: { message: string }) =>
            of(loadPackagesFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const loadPackagingTypesEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(loadPackagingTypes),
      concatMap((data: { packageType: EnumPackageType, transportMode: TransportMode }) =>
        easyDgdService.loadPackagingTypes(data.packageType, data.transportMode).pipe(
          map((packagingTypes: PackagingType[]) => {
            if (data.packageType === EnumPackageType.Tank) {
              return loadPackagingTypesTankSuccess({ packagingTypes, packageType: data.packageType })
            }
            if (data.packageType === EnumPackageType.Bulk) {
              return loadPackagingTypesBulkSuccess({ packagingTypes, packageType: data.packageType })
            }
            return loadPackagingTypesSuccess({ packagingTypes, packageType: data.packageType })
          }),
          catchError((error: { message: string }) =>
            of(loadPackagingTypesFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const loadUnPackageTypesEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(loadUnPackagingTypes),
      exhaustMap((data: { packagingTypeId: number, transportMode: TransportMode, packageType: EnumPackageType }) =>
        easyDgdService.loadUnPackagingTypes(data.packagingTypeId, data.transportMode).pipe(
          map((unPackagingTypes: PackagingType[]) => loadUnPackagingTypesSuccess({ unPackagingTypes, packageType: data.packageType })),
          catchError((error: { message: string }) =>
            of(loadUnPackagingTypesFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const initializeSinglePackageEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(initializeSinglePackage),
      exhaustMap((data: { dangerousGood: DangerousGoodItem, packageType: EnumPackageType, transportMode: TransportMode }) =>
        easyDgdService.initializeTempPackage(data.dangerousGood.ID, data.packageType, data.transportMode).pipe(
          map((model: PackagingModel) => model as SinglePackagingModel),
          map((model: SinglePackagingModel) => initializeSinglePackageSuccess({
            pkg: {
              ...model,
              DangerousGood: data.dangerousGood
            }
          })),
          catchError((error: { message: string }) =>
            of(initializeSinglePackageFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const initializeInnerPackageEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(initializeInnerPackage),
      exhaustMap((data: { dangerousGood: DangerousGoodItem, packageType: EnumPackageType, transportMode: TransportMode, internalId: string }) =>
        easyDgdService.initializeTempPackage(data.dangerousGood.ID, data.packageType, data.transportMode).pipe(
          map((model: PackagingModel) => model as InnerPackagingModel),
          map((model: InnerPackagingModel) => initializeInnerPackageSuccess({
            pkg: {
              ...model,
              DangerousGood: data.dangerousGood,
              InternalId: data.internalId
            }
          })),
          catchError((error: { message: string }) =>
            of(initializeInnerPackageFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const saveSinglePackageEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(saveSinglePackage),
      exhaustMap((data: { pkg: SinglePackagingModel }) =>
        easyDgdService.saveSinglePackage(mergeDecimals<SinglePackagingModel>(data.pkg)).pipe(
          map((model: SinglePackagingModel) => {
            if (!data.pkg.Id) {
              return saveSinglePackageSuccess({
                pkg: {
                  ...model,
                  DangerousGood: data.pkg.DangerousGood
                }
              });
            } else {
              return saveSinglePackageUpdateSuccess({
                pkg: {
                  ...model,
                  DangerousGood: data.pkg.DangerousGood
                }
              });
            }
          }),
          catchError((error: { message: string }) =>
            of(saveSinglePackageFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const duplicateSinglePackageEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(duplicateSinglePackage),
      exhaustMap((data: { pkg: SinglePackagingModel }) =>
        easyDgdService.duplicateSinglePackage(data.pkg).pipe(
          map((model: SinglePackagingModel) => duplicateSinglePackageSuccess({
            pkg: {
              ...model,
              DangerousGood: data.pkg.DangerousGood
            }
          })),
          catchError((error: { message: string }) =>
            of(duplicateSinglePackageFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const removeSinglePackageEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(removeSinglePackage),
      exhaustMap((data: { id: string }) =>
        easyDgdService.removeSinglePackage(data.id).pipe(
          map(() => removeSinglePackageSuccess({
            id: data.id
          })),
          catchError((error: { message: string }) =>
            of(removeSinglePackageFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const saveCompositePackageEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(saveCompositePackage),
      exhaustMap((data: { pkg: CompositePackagingModel }) =>
        easyDgdService.saveCompositePackage(mergeDecimalsForComposite(data.pkg)).pipe(
          map((model: CompositePackagingModel) => {
            if (!data.pkg.Id) {
              return saveCompositePackageSuccess({
                pkg: {
                  ...model,
                  Items: [
                    ...model.Items.map(i => ({
                      ...i,
                      DangerousGood: data.pkg.Items.find(m => m.InternalId === i.InternalId)?.DangerousGood
                    }))
                  ]
                }
              });
            } else {
              return saveCompositePackageUpdateSuccess({
                pkg: {
                  ...model,
                  Items: [
                    ...model.Items.map(i => ({
                      ...i,
                      DangerousGood: data.pkg.Items.find(m => m.InternalId === i.InternalId)?.DangerousGood
                    }))
                  ]
                }
              });
            }
          }),
          catchError((error: { message: string }) =>
            of(saveCompositePackageFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const duplicateCompositePackageEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(duplicateCompositePackage),
      exhaustMap((data: { pkg: CompositePackagingModel }) =>
        easyDgdService.duplicateCompositePackage(data.pkg).pipe(
          map((model: CompositePackagingModel) => duplicateCompositePackageSuccess({
            pkg: {
              ...model,
              Items: [
                ...model.Items.map(i => ({
                  ...i,
                  DangerousGood: data.pkg.Items.find(m => m.InternalId === i.InternalId)?.DangerousGood
                }))
              ]
            }
          })),
          catchError((error: { message: string }) =>
            of(duplicateCompositePackageFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const removeCompositePackageEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(removeCompositePackage),
      exhaustMap((data: { id: string }) =>
        easyDgdService.removeCompositePackage(data.id).pipe(
          map(() => removeCompositePackageSuccess({
            id: data.id
          })),
          catchError((error: { message: string }) =>
            of(removeCompositePackageFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const addOverpackEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(addOverpack),
      exhaustMap((data: { pkg: Partial<OverPackagingModel>, packageId: string }) =>
        easyDgdService.addOverpack(data.pkg).pipe(
          switchMap((pkg: OverPackagingModel) =>
          ([
            addOverpackSuccess({ pkg: pkg }),
            assignOverpack({ packageId: data.packageId, overpackId: pkg.Id })
          ])
          ),
          catchError((error: { message: string }) =>
            of(removeCompositePackageFailure({ errorMsg: error.message }))
          )
        )
      ),
      catchError((error: { message: string }) =>
        of(removeCompositePackageFailure({ errorMsg: error.message }))
      )
    );
  },
  { functional: true }
);

export const updateOverpackEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(updateOverpack),
      exhaustMap((data: { pkg: OverPackagingModel }) =>
        easyDgdService.updateOverpack(data.pkg).pipe(
          map((pkg: OverPackagingModel) => updateOverpackSuccess({
            pkg: pkg,
          })),
          catchError((error: { message: string }) =>
            of(removeCompositePackageFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const assignOverpackEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(assignOverpack),
      exhaustMap((data: { packageId: string, overpackId: string }) =>
        easyDgdService.assignOverpack(data.packageId, data.overpackId).pipe(
          map(() => assignOverpackSuccess({ packageId: data.packageId, overpackId: data.overpackId })),
          catchError((error: { message: string }) =>
            of(assignOverpackFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const removeOverpackEffect = createEffect(
  (actions$ = inject(Actions), easyDgdService = inject(EasyDgdService)) => {
    return actions$.pipe(
      ofType(removeOverpack),
      exhaustMap((data: { pkg: OverPackagingModel }) =>
        easyDgdService.removeOverpack(data.pkg).pipe(
          map(() => removeOverpackSuccess({ pkg: data.pkg })),
          catchError((error: { message: string }) =>
            of(assignOverpackFailure({ errorMsg: error.message }))
          )
        )
      )
    );
  },
  { functional: true }
);

const mergeDecimals = <T extends InnerPackagingModel>(pkg: T) => {
  return {
    ...pkg,
    NetWeightValue: pkg.NetWeightValue?.replace(',', '.'),
    NetVolumeValue: pkg.NetVolumeValue?.replace(',', '.'),
    NetExplosiveMassValue: pkg.NetExplosiveMassValue?.replace(',', '.'),
    GrossWeightValue: pkg.GrossWeightValue?.replace(',', '.'),
  }
}

const mergeDecimalsForComposite = (pkg: CompositePackagingModel) => {
  return {
    ...pkg,
    GrossWeightValue: pkg.GrossWeightValue.replace(',', '.'),
  }
}

